/**
 * OTHER-SPECIFY-1 (s155, D-155.6) — shared helpers for the "Other → tell us more" pattern.
 *
 * A select field flagged `specifyOnOther` (generic_fields.specify_on_other, from the spec) reveals a
 * NON-REQUIRED free-text box when its "Other"-type choice is selected; the text persists to
 * generic_entries.specify_text via the SAME generic-entries write (single-writer). "Other" alone stays
 * a complete, honest answer — specify_text NEVER enters completion/ERS (D-155.6, audit-(a) premise).
 * These helpers are the single source for detecting the trigger option + rendering "Other — {text}".
 */

/** True when a select VALUE is an "Other"-type catch-all (case-insensitive). Covers "Other",
 *  "Other / unsure", "Other Legal Document", "Other Family", "Neither (I live with family / other)".
 *  The specify box only shows when the field is ALSO specifyOnOther, so this is just the value gate. */
export function isOtherOption(value: string | null | undefined): boolean {
  return !!value && /other/i.test(value);
}

/** True when this field + selected value should show / persist a specify box. */
export function specifyApplies(
  specifyOnOther: boolean | null | undefined,
  value: string | null | undefined,
): boolean {
  return specifyOnOther === true && isOtherOption(value);
}

/** Planner/executor display for a select value that may carry a specify detail:
 *  "Other — {text}" when the value is an Other option AND a detail is present; else just the value. */
export function formatSelectWithSpecify(
  value: string | null | undefined,
  specifyText: string | null | undefined,
): string {
  const v = (value ?? "").trim();
  const s = (specifyText ?? "").trim();
  return v && s && isOtherOption(v) ? `${v} — ${s}` : v;
}
